import { useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FileUpload } from "@/components/ui/file-upload";
import { fileToBase64, FileValidationError } from "@/utils/fileUtils";

export interface VoucherkuFieldsSectionProps {
  readonly title: string;
  readonly titleError?: string;
  readonly description: string;
  readonly descriptionError?: string;
  readonly termsAndConditions: string;
  readonly termsAndConditionsError?: string;
  readonly bannerImage: string;
  readonly bannerImageError?: string;
  readonly onChangeTitle: (value: string) => void;
  readonly onChangeDescription: (value: string) => void;
  readonly onChangeTermsAndConditions: (value: string) => void;
  readonly onChangeBannerImage: (value: string) => void;
}

export const VoucherkuFieldsSection = ({
  title,
  titleError,
  description,
  descriptionError,
  termsAndConditions,
  termsAndConditionsError,
  bannerImage,
  bannerImageError,
  onChangeTitle,
  onChangeDescription,
  onChangeTermsAndConditions,
  onChangeBannerImage,
}: VoucherkuFieldsSectionProps): React.JSX.Element => {
  const [isConverting, setIsConverting] = useState(false);
  const [conversionError, setConversionError] = useState<string | null>(null);

  const handleBannerImageChange = useCallback(
    async (_fileName: string, file?: File | null) => {
      setConversionError(null);

      // If no file, clear the banner image
      if (!file) {
        onChangeBannerImage("");
        return;
      }

      setIsConverting(true);
      try {
        const result = await fileToBase64(file);
        onChangeBannerImage(result.base64);
      } catch (error) {
        if (error instanceof FileValidationError) {
          setConversionError(error.message);
        } else {
          setConversionError("Failed to process image file");
        }
        onChangeBannerImage("");
      } finally {
        setIsConverting(false);
      }
    },
    [onChangeBannerImage]
  );

  const displayBannerError = bannerImageError ?? conversionError ?? undefined;

  return (
    <>
      {/* Title and Description row */}
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="voucherku-title"
            className="text-sm font-semibold text-gray-800 text-left"
          >
            Title
          </label>
          <Input
            id="voucherku-title"
            type="text"
            value={title}
            onChange={(event) => onChangeTitle(event.target.value)}
            placeholder="Title"
            errorMessage={titleError}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="voucherku-description"
            className="text-sm font-semibold text-gray-800 text-left"
          >
            Description
          </label>
          <Textarea
            id="voucherku-description"
            value={description}
            onChange={(event) => onChangeDescription(event.target.value)}
            placeholder="Description"
            rows={4}
            className={descriptionError ? "border-red-600" : ""}
          />
          {descriptionError && (
            <span className="text-xs text-red-600">{descriptionError}</span>
          )}
        </div>
      </div>

      {/* Terms & Conditions and Banner Image row */}
      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="voucherku-terms"
            className="text-sm font-semibold text-gray-800 text-left"
          >
            Terms &amp; Conditions
          </label>
          <Textarea
            id="voucherku-terms"
            value={termsAndConditions}
            onChange={(event) => onChangeTermsAndConditions(event.target.value)}
            placeholder="Terms &amp; Conditions"
            rows={4}
            className={termsAndConditionsError ? "border-red-600" : ""}
          />
          {termsAndConditionsError && (
            <span className="text-xs text-red-600">
              {termsAndConditionsError}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="voucherku-banner"
            className="text-sm font-semibold text-gray-800 text-left"
          >
            Banner Image
          </label>
          <FileUpload
            id="voucherku-banner"
            value={bannerImage}
            onChange={handleBannerImageChange}
            accept="image/*"
            acceptText="File type: .jpeg or .png"
            maxSizeText="Maximum size: 5MB"
            variant="image"
            errorMessage={displayBannerError}
          />
          {isConverting && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
              <span>Processing image...</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

